import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/Terms.css';

const Terms = () => {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <div className="terms-container">
      <nav className="terms-nav">
        <div className="nav-content">
          <div className="logo" onClick={handleLogoClick}>
            Equation Live
          </div>
          <div className="nav-buttons">
            <button className="nav-button primary" onClick={() => navigate('/signin')}>
              Sign In
            </button>
          </div>
        </div>
      </nav>

      <main className="terms-content">
        <div className="terms-header">
          <h1>Terms of Use</h1>
          <p>Effective: July 6, 2025</p>
        </div>

        <div className="terms-body">
          <section className="terms-section">
            <p>Thank you for using Equation Live!</p>
            
            <p>These Terms of Use apply to your use of Equation Live's browser extension and associated services, including our website and any related software applications (all together, "Services"). These Terms form an agreement between you and Equation Live, and they include important provisions for resolving disputes. By using our Services, you agree to these Terms.</p>
            
            <p>Our Privacy Policy explains how we collect and use personal information. Although it does not form part of these Terms, it is an important document that you should read.</p>
          </section>

          <section className="terms-section">
            <h2>Who We Are</h2>
            <p>Equation Live is a technology company focused on making LaTeX editing and rendering easy in ChatGPT and other platforms. Our mission is to provide seamless mathematical expression capabilities to enhance user productivity and communication.</p>
          </section>

          <section className="terms-section">
            <h2>Registration and Access</h2>
            
            <h3>Minimum Age</h3>
            <p>You must be at least 13 years old or the minimum age required in your country to consent to use the Services. If you are under 18, you must have your parent or legal guardian's permission to use the Services.</p>
            
            <h3>Registration</h3>
            <p>You must provide accurate and complete information to register for an account to use our Services. You may not share your account credentials or make your account available to anyone else and are responsible for all activities that occur under your account. If you create an account or use the Services on behalf of another person or entity, you must have the authority to accept these Terms on their behalf.</p>
          </section>

          <section className="terms-section">
            <h2>Using Our Services</h2>
            
            <h3>What You Can Do</h3>
            <p>Subject to your compliance with these Terms, you may access and use our Services. In using our Services, you must comply with all applicable laws as well as our Usage Policies and any other documentation, guidelines, or policies we make available to you.</p>
            
            <h3>What You Cannot Do</h3>
            <p>You may not use our Services for any illegal, harmful, or abusive activity. For example, you may not:</p>
            <ul>
              <li>Use our Services in a way that infringes, misappropriates, or violates anyone's rights</li>
              <li>Modify, copy, lease, sell, or distribute any of our Services</li>
              <li>Attempt to or assist anyone to reverse engineer, decompile, or discover the source code or underlying components of our Services, including our algorithms or systems (except to the extent this restriction is prohibited by applicable law)</li>
              <li>Automatically or programmatically extract data or Output (defined below)</li>
              <li>Represent that Output was human-generated when it was not</li>
              <li>Interfere with or disrupt our Services, including circumvent any rate limits or restrictions or bypass any protective measures we put on our Services</li>
              <li>Use Output to develop services that compete with Equation Live</li>
            </ul>
            
            <h3>Software</h3>
            <p>Our Services may allow you to download software, such as browser extensions, which may update automatically to ensure you're using the latest version. Our software may include open source software that is governed by its own licenses that we've made available to you.</p>
            
            <h3>Third Party Services</h3>
            <p>Our services may include third party software, products, or services ("Third Party Services") and some parts of our Services may include output from those services ("Third Party Output"). Third Party Services and Third Party Output are subject to their own terms, and we are not responsible for them.</p>
            
            <h3>Feedback</h3>
            <p>We appreciate your feedback, and you agree that we may use it without restriction or compensation to you.</p>
          </section>

          <section className="terms-section">
            <h2>Content</h2>
            
            <h3>Your Content</h3>
            <p>You may provide input to the Services ("Input"), and receive output from the Services based on the Input ("Output"). Input and Output are collectively "Content." You are responsible for Content, including ensuring that it does not violate any applicable law or these Terms. You represent and warrant that you have all rights, licenses, and permissions needed to provide Input to our Services.</p>
            
            <h3>Ownership of Content</h3>
            <p>As between you and Equation Live, and to the extent permitted by applicable law, you (a) retain your ownership rights in Input and (b) own the Output. We hereby assign to you all our right, title, and interest, if any, in and to Output.</p>
            
            <h3>Similarity of Content</h3>
            <p>Due to the nature of our Services and mathematical rendering generally, output may not be unique and other users may receive similar output from our Services. Our assignment above does not extend to other users' output or any Third Party Output.</p>
            
            <h3>Our Use of Content</h3>
            <p>We may use Content to provide, maintain, develop, and improve our Services, comply with applicable law, enforce our terms and policies, and keep our Services safe.</p>
            
            <h3>Accuracy</h3>
            <p>Mathematical rendering and LaTeX processing are complex technical processes. We are constantly working to improve our Services to make them more accurate, reliable, safe, and beneficial. Given the technical nature of mathematical expression processing, use of our Services may, in some situations, result in Output that does not accurately reflect the intended mathematical expression.</p>
            
            <p>When you use our Services you understand and agree:</p>
            <ul>
              <li>Output may not always be accurate. You should not rely on Output from our Services as a sole source of truth for mathematical expressions, or as a substitute for professional mathematical verification.</li>
              <li>You must evaluate Output for accuracy and appropriateness for your use case, including using human review as appropriate, before using or sharing Output from the Services.</li>
              <li>You must not use any Output for any purpose that could have a legal or material impact, such as making educational, employment, housing, insurance, legal, medical, or other important decisions.</li>
              <li>Our Services may provide incomplete, incorrect, or inappropriate Output that does not represent Equation Live's views.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>Our IP Rights</h2>
            <p>We and our affiliates own all rights, title, and interest in and to the Services. You may only use our name and logo in accordance with our Brand Guidelines.</p>
          </section>

          <section className="terms-section">
            <h2>Paid Accounts</h2>
            
            <h3>Billing</h3>
            <p>If you purchase any Services, you will provide complete and accurate billing information, including a valid payment method. For paid subscriptions, we will automatically charge your payment method on each agreed-upon periodic renewal until you cancel. You're responsible for all applicable taxes, and we'll charge tax when required. If your payment cannot be completed, we may downgrade your account or suspend your access to our Services until payment is received.</p>
            
            <h3>Subscription Plans</h3>
            <p>We offer the following subscription plans:</p>
            <ul>
              <li><strong>Trial:</strong> Free for 30 days - Automaticlly enabled on sign-up (no card required) giving you full extension access, dark mode support, real-time rendering</li>
              <li><strong>Basic:</strong> $4.99/month - Full extension access, dark mode support, real-time rendering</li>
            </ul>
            
            <h3>Cancellation</h3>
            <p>You can cancel your paid subscription at any time. Payments are non-refundable, except where required by law. These Terms do not override any mandatory local laws regarding your cancellation rights.</p>
            
            <h3>Changes</h3>
            <p>We may change our prices from time to time. If we increase our subscription prices, we will give you at least 30 days' notice and any price increase will take effect on your next renewal so that you can cancel if you do not agree to the price increase.</p>
          </section>

          <section className="terms-section">
            <h2>Termination and Suspension</h2>
            
            <h3>Termination</h3>
            <p>You are free to stop using our Services at any time. We reserve the right to suspend or terminate your access to our Services or delete your account if we determine:</p>
            <ul>
              <li>You breached these Terms or our Usage Policies</li>
              <li>We must do so to comply with the law</li>
              <li>Your use of our Services could cause risk or harm to Equation Live, our users, or anyone else</li>
            </ul>
            <p>We also may terminate your account if it has been inactive for over a year and you do not have a paid account. If we do, we will provide you with advance notice.</p>
            
            <h3>Appeals</h3>
            <p>If you believe we have suspended or terminated your account in error, you can file an appeal with us by contacting our Support team.</p>
          </section>

          <section className="terms-section">
            <h2>Discontinuation of Services</h2>
            <p>We may decide to discontinue our Services, but if we do, we will give you advance notice and a refund for any prepaid, unused Services.</p>
          </section>

          <section className="terms-section">
            <h2>Disclaimer of Warranties</h2>
            <p>OUR SERVICES ARE PROVIDED "AS IS." EXCEPT TO THE EXTENT PROHIBITED BY LAW, WE AND OUR AFFILIATES AND LICENSORS MAKE NO WARRANTIES (EXPRESS, IMPLIED, STATUTORY OR OTHERWISE) WITH RESPECT TO THE SERVICES, AND DISCLAIM ALL WARRANTIES INCLUDING, BUT NOT LIMITED TO, WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, SATISFACTORY QUALITY, NON-INFRINGEMENT, AND QUIET ENJOYMENT, AND ANY WARRANTIES ARISING OUT OF ANY COURSE OF DEALING OR TRADE USAGE. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ACCURATE OR ERROR FREE, OR THAT ANY CONTENT WILL BE SECURE OR NOT LOST OR ALTERED.</p>
            
            <p>YOU ACCEPT AND AGREE THAT ANY USE OF OUTPUTS FROM OUR SERVICE IS AT YOUR SOLE RISK AND YOU WILL NOT RELY ON OUTPUT AS A SOLE SOURCE OF TRUTH OR FACTUAL INFORMATION, OR AS A SUBSTITUTE FOR PROFESSIONAL ADVICE.</p>
          </section>

          <section className="terms-section">
            <h2>Limitation of Liability</h2>
            <p>NEITHER WE NOR ANY OF OUR AFFILIATES OR LICENSORS WILL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, OR DATA OR OTHER LOSSES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR AGGREGATE LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE GREATER OF THE AMOUNT YOU PAID FOR THE SERVICE THAT GAVE RISE TO THE CLAIM DURING THE 12 MONTHS BEFORE THE LIABILITY AROSE OR ONE HUNDRED DOLLARS ($100). THE LIMITATIONS IN THIS SECTION APPLY ONLY TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.</p>
            
            <p>Some countries and states do not allow the disclaimer of certain warranties or the limitation of certain damages, so some or all of the terms above may not apply to you, and you may have additional rights. In that case, these Terms only limit our responsibilities to the maximum extent permissible in your country of residence.</p>
          </section>

          <section className="terms-section">
            <h2>Indemnity</h2>
            <p>If you are a business or organization, to the extent permitted by law, you will indemnify and hold harmless us, our affiliates, and our personnel, from and against any costs, losses, liabilities, and expenses (including attorneys' fees) from third party claims arising out of or relating to your use of the Services and Content or any violation of these Terms.</p>
          </section>

          <section className="terms-section">
            <h2>Dispute Resolution</h2>
            <p>Before filing a claim against Equation Live, you agree to try to resolve the dispute informally by contacting us first. We'll try to resolve the dispute informally by contacting you via email. If we can't resolve the dispute within 60 days, you may bring a formal proceeding.</p>
            
            <p>Any claims arising out of or relating to these Terms or the Services will be governed by the laws of the United States, without regard to its conflict of laws principles.</p>
          </section>

          <section className="terms-section">
            <h2>Copyright Complaints</h2>
            <p>If you believe that your intellectual property rights have been infringed, please send notice to support@equationlive.com. We may delete or disable content that we believe violates these Terms or is alleged to be infringing and will terminate accounts of repeat infringers where appropriate.</p>
          </section>

          <section className="terms-section">
            <h2>General Terms</h2>
            
            <h3>Assignment</h3>
            <p>You may not assign or transfer any rights or obligations under these Terms and any attempt to do so will be void. We may assign our rights or obligations under these Terms to any affiliate, subsidiary, or successor in interest of any business associated with our Services.</p>
            
            <h3>Changes to These Terms or Our Services</h3>
            <p>We are continuously working to develop and improve our Services. We may update these Terms or our Services accordingly from time to time. We will give you at least 30 days advance notice of changes to these Terms that materially adversely impact you either via email or an in-product notification. All other changes will be effective as soon as we post them to our website. If you do not agree to the changes, you must stop using our Services.</p>
            
            <h3>Delay in Enforcing These Terms</h3>
            <p>Our failure to enforce a provision is not a waiver of our right to do so later. If any portion of these Terms is determined to be invalid or unenforceable, that portion will be enforced to the maximum extent permissible and it will not affect the enforceability of any other terms.</p>
            
            <h3>Entire Agreement</h3>
            <p>These Terms contain the entire agreement between you and Equation Live regarding the Services and supersedes any prior or contemporaneous agreements between you and Equation Live.</p>
            
            <h3>Governing Law</h3>
            <p>United States law will govern these Terms except for its conflicts of laws principles. All claims arising out of or relating to these Terms will be brought exclusively in the federal or state courts of the United States.</p>
          </section>

          <section className="terms-section">
            <h2>Third-Party Terms of Service</h2>
            <p>Our Services may integrate with or link to third-party services. We encourage you to review the terms of service of these third-party services to understand their terms and conditions:</p>
            
            <div className="third-party-links">
              <div className="third-party-card">
                <div className="third-party-content">
                  <h3>ChatGPT</h3>
                  <p>Our extension integrates with ChatGPT. Review their terms of use to understand how they handle your data and content.</p>
                  <a href="https://openai.com/policies/terms-of-use" target="_blank" rel="noopener noreferrer" className="third-party-button">
                    View ChatGPT Terms
                  </a>
                </div>
              </div>
              
              <div className="third-party-card">
                <div className="third-party-content">
                  <h3>Google Chrome</h3>
                  <p>Our extension operates within the Google Chrome browser. Review their terms of service for browser-related policies.</p>
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="third-party-button">
                    View Chrome Terms
                  </a>
                </div>
              </div>
            </div>
            
            <p>Please note that we are not responsible for the terms of service of these third-party services. Your use of these services is subject to their respective terms of service and privacy policies.</p>
          </section>

          <section className="terms-section">
            <h2>Contact Information</h2>
            <p>If you have any questions about these Terms of Use, please contact us at:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> support@equationlive.com</p>
              <p><strong>Support:</strong> <a href="/support">Contact Support</a></p>
            </div>
          </section>
        </div>
      </main>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Equation Live</h4>
            <p>Making LaTeX editing and rendering easy in ChatGPT</p>
          </div>
          <div className="footer-section">
            <ul>
              <li><a href="/support">Support</a></li>
              <li><a href="/about">About</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/terms">Terms of Service</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Equation Live. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Terms; 
